/**
 * Client-side offline expense queue: when a new-expense submission can't reach the
 * server (see expense-form-dialog.tsx), it's appended here (localStorage, per-trip
 * key) instead, and later replayed by flushQueue via the trip's normal
 * createExpenseAction — see offline-expense-flush.ts. Pure storage + lock logic only;
 * no React/network code lives here so it's directly unit-testable.
 */

/**
 * Structural shape of expense-actions.ts's ExpenseFormInput, duplicated here (rather
 * than imported) so this module stays free of any "use server" file import — it must
 * also load and run fine in a Vitest "node" test environment.
 */
export type ExpenseFormInputLike = {
  title: string;
  category: string;
  amount: string;
  currency: string;
  fxRateToBase: string;
  dayId?: string | null;
  paidFromPot: boolean;
  paidByMemberId?: string | null;
  memo?: string | null;
  receiptPhotoUrl?: string | null;
  splits: { memberId: string; shareAmount: string }[];
};

export type QueuedExpense = {
  clientId: string;
  createdAt: string;
  input: ExpenseFormInputLike;
  /** Last flush attempt's validation error message, or null if never attempted / not yet failed. */
  error: string | null;
};

const QUEUE_KEY_PREFIX = "offlineExpenseQueue:";
const LOCK_KEY_PREFIX = "offlineExpenseQueueLock:";
/**
 * Dispatched on `window` after every queue mutation (enqueue/remove/update) so any
 * mounted OfflineExpenseQueueSection re-reads localStorage and re-renders — the
 * dialog that enqueues a new item and the section that displays the queue are
 * separate component trees with no other shared state.
 */
export const QUEUE_CHANGE_EVENT = "offlineExpenseQueueChange";
// Stale-lock recovery window: if a tab crashes (or its flush hangs) mid-flush, the
// localStorage lock would otherwise block every other tab forever — this bounds that.
const LOCK_TTL_MS = 30_000;

// ponytail: single-JS-realm guard only. True cross-tab mutual exclusion (e.g. via the
// Web Locks API or BroadcastChannel) would also let a live second tab wait instead of
// bailing out — add that if multi-tab flush contention turns out to matter in practice.
const inFlightTripIds = new Set<string>();

function hasStorage(): boolean {
  return typeof localStorage !== "undefined";
}

function queueKey(tripId: string): string {
  return `${QUEUE_KEY_PREFIX}${tripId}`;
}

/**
 * The localStorage key a trip's queue is stored under — exported so other tabs can
 * filter the native `storage` event (fired on every *other* same-origin document when
 * one tab writes to localStorage; never in the tab that made the write, which is what
 * QUEUE_CHANGE_EVENT above is for) down to just this trip's key.
 */
export function queueStorageKey(tripId: string): string {
  return queueKey(tripId);
}

function lockKey(tripId: string): string {
  return `${LOCK_KEY_PREFIX}${tripId}`;
}

/** All queued items for a trip, oldest first. Never throws — corrupt/missing storage reads as empty. */
export function loadQueue(tripId: string): QueuedExpense[] {
  if (!hasStorage()) return [];
  try {
    const raw = localStorage.getItem(queueKey(tripId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveQueue(tripId: string, items: QueuedExpense[]): void {
  if (!hasStorage()) return;
  localStorage.setItem(queueKey(tripId), JSON.stringify(items));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(QUEUE_CHANGE_EVENT, { detail: { tripId } }));
  }
}

/**
 * Appends a new queued item and returns it. `clientId` is supplied by the caller
 * (expense-form-dialog.tsx generates it once when the dialog opens and reuses it for
 * every attempt — see I1) rather than generated here: the same clientId must already
 * have been sent on the direct createExpenseAction attempt that failed, so that if
 * that attempt actually committed server-side and only the response was lost, the
 * eventual flush's onConflictDoNothing dedup catches it instead of creating a second
 * expense.
 */
export function enqueueExpense(tripId: string, clientId: string, input: ExpenseFormInputLike): QueuedExpense {
  const item: QueuedExpense = {
    clientId,
    createdAt: new Date().toISOString(),
    input,
    error: null,
  };
  saveQueue(tripId, [...loadQueue(tripId), item]);
  return item;
}

/** Removes a queued item (successfully flushed, or user-deleted). No-op if not found. */
export function removeQueuedExpense(tripId: string, clientId: string): void {
  saveQueue(
    tripId,
    loadQueue(tripId).filter((item) => item.clientId !== clientId)
  );
}

/** Patches a queued item's `input` and/or `error` (e.g. after an edited retry, or a failed flush attempt). */
export function updateQueuedExpense(
  tripId: string,
  clientId: string,
  patch: Partial<Pick<QueuedExpense, "input" | "error">>
): void {
  saveQueue(
    tripId,
    loadQueue(tripId).map((item) => (item.clientId === clientId ? { ...item, ...patch } : item))
  );
}

/**
 * Single-flight guard for flushQueue: true if the caller may proceed (and now holds
 * the lock — always pair with releaseFlushLock), false if a flush for this trip is
 * already in progress (this tab, via the module flag) or was started recently by
 * another tab (the localStorage timestamp lock — see the multi-tab caveat above).
 */
export function tryAcquireFlushLock(tripId: string): boolean {
  if (inFlightTripIds.has(tripId)) return false;
  if (hasStorage()) {
    const existing = Number(localStorage.getItem(lockKey(tripId)) ?? "0");
    if (existing && Date.now() - existing < LOCK_TTL_MS) return false;
    localStorage.setItem(lockKey(tripId), String(Date.now()));
  }
  inFlightTripIds.add(tripId);
  return true;
}

export function releaseFlushLock(tripId: string): void {
  inFlightTripIds.delete(tripId);
  if (hasStorage()) localStorage.removeItem(lockKey(tripId));
}
